import {getYYYYMMddFormat} from '@di-shared/api/di/utils';
import {parseDateWithFormat, subDate} from '@shared/dateUtils';

export const PERIOD_KR = {
  SELECT_MONTH: 12,
  RECENT_30_DAYS: 30,
  RECENT_90_DAYS: 90,
};

export const HISTORY_FILTER_PERIOD_KR = {
  SELECT_MONTH: 'select-month',
  RECENT_30_DAYS: 'recent-30-days',
  RECENT_90_DAYS: 'recent-90-days',
  CUSTOM_DAYS: 'custom-days',
} as const;

export type HistoryFilterPeriodKr = (typeof HISTORY_FILTER_PERIOD_KR)[keyof typeof HISTORY_FILTER_PERIOD_KR];

export interface HistoryFilterFormStateKr {
  period: HistoryFilterPeriodKr;
  startDate: Date | null;
  endDate: Date | null;
  tagIds: Array<string>;
}

export interface HistoryFilterQueryStateKr extends HistoryFilterFormStateKr {
  sourceDate: Date | null;
}

function isValidPeriod(period: string | null): period is HistoryFilterPeriodKr {
  return !!period && Object.values(HISTORY_FILTER_PERIOD_KR).includes(period as HistoryFilterPeriodKr);
}

export function getHistoryFilterPeriodState(period: HistoryFilterPeriodKr, today = new Date()) {
  switch (period) {
    case HISTORY_FILTER_PERIOD_KR.SELECT_MONTH:
      // 실제 날짜는 BOTTOM_SHEET_HISTORY_SELECT_MONTH에서 고른 월로 호출부에서 직접 세팅함
      return {period, startDate: null, endDate: null};
    case HISTORY_FILTER_PERIOD_KR.RECENT_30_DAYS:
      return {
        period,
        startDate: subDate(today, {days: PERIOD_KR.RECENT_30_DAYS - 1}),
        endDate: today,
      };
    case HISTORY_FILTER_PERIOD_KR.RECENT_90_DAYS:
      return {
        period,
        startDate: subDate(today, {days: PERIOD_KR.RECENT_90_DAYS - 1}),
        endDate: today,
      };
    case HISTORY_FILTER_PERIOD_KR.CUSTOM_DAYS:
      return {
        period,
        startDate: null,
        endDate: null,
      };
  }
}

export function getDefaultHistoryFilterState(today = new Date()): HistoryFilterQueryStateKr {
  const defaultPeriodState = getHistoryFilterPeriodState(HISTORY_FILTER_PERIOD_KR.RECENT_30_DAYS, today);

  return {
    ...defaultPeriodState,
    sourceDate: null,
    tagIds: [],
  };
}

export function parseHistoryFilterSearchParams(searchParams: URLSearchParams, today = new Date()): HistoryFilterQueryStateKr {
  const defaultState = getDefaultHistoryFilterState(today);
  const period = searchParams.get('period');
  const validatedPeriod = isValidPeriod(period) ? period : defaultState.period;
  const periodState = getHistoryFilterPeriodState(validatedPeriod, today);
  const startDate = parseDateWithFormat(searchParams.get('startDate')) || periodState.startDate;
  const endDate = parseDateWithFormat(searchParams.get('endDate')) || periodState.endDate;

  return {
    sourceDate: parseDateWithFormat(searchParams.get('sourceDate')),
    period: validatedPeriod,
    startDate,
    endDate,
    tagIds: searchParams.getAll('tag').filter(Boolean),
  };
}

export function createHistoryFilterSearchParams(state: HistoryFilterQueryStateKr) {
  const params = new URLSearchParams();

  params.set('period', state.period);

  if (state.sourceDate) {
    params.set('sourceDate', getYYYYMMddFormat(state.sourceDate));
  }

  if (state.startDate) {
    params.set('startDate', getYYYYMMddFormat(state.startDate));
  }

  if (state.endDate) {
    params.set('endDate', getYYYYMMddFormat(state.endDate));
  }

  state.tagIds.forEach((tagId) => {
    params.append('tag', tagId);
  });

  return params;
}

export function createHistoryFilterSearch(state: HistoryFilterQueryStateKr) {
  return `?${createHistoryFilterSearchParams(state).toString()}`;
}

export function getHistoryFilterFormState(state: HistoryFilterQueryStateKr): HistoryFilterFormStateKr {
  return {
    period: state.period,
    startDate: state.startDate,
    endDate: state.endDate,
    tagIds: state.tagIds,
  };
}
